import { MAIN_CONTENT_HEIGHT } from '@/catalog-apps/components/config'
import { PageHeader } from '@/components/ui/PageHeader'
import { useCurrentProject } from './useCurrentProject'
import { IconV2EditOutline } from '@icons/IconV2EditOutline'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import { OrgProjectForm } from '@/pages/Subscriptions/Forms/OrgProjectForm'
import { getOrganisationPermissions, getProjectOrganisationId, useUserLinkedOrganisations } from './useUserLinkedProjects'
import { ProjectsBreadcrumbs } from './ProjectsBreadcrumbs'
import { ProjectDetailsLayout, ProjectLinkedWorkspacesPanel, ProjectWorkspacesSection } from './ProjectDetailsLayout'
import clsx from 'clsx'
import { useMemo } from 'react'

export function ProjectOverviewPage() {
  const project = useCurrentProject()
  const organisations = useUserLinkedOrganisations()
  const organisationId = getProjectOrganisationId(project)
  const orgPermissions = getOrganisationPermissions(organisations, organisationId)
  const organisationProject = useMemo(
    () => ({
      projectId: project.projectId,
      name: project.name,
      isFrozen: project.isFrozen,
      workspaceCount: project.workspaceCount,
      leaders: project.leaders,
      administrators: project.administrators,
    }),
    [project]
  )

  return (
    <div className="flex flex-col" style={{ height: MAIN_CONTENT_HEIGHT }}>
      <PageHeader className="shrink-0 pr-3" title={<ProjectsBreadcrumbs title="Overview" />}>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            <span className="font-bold">{project.workspaceCount}</span> Workspace
            {project.workspaceCount > 1 || project.workspaceCount === 0 ? 's' : ''}
          </Badge>
          <Badge variant="outline" className={clsx('text-xs', { hidden: !project.frequency })}>
            Frequency: <span className="font-bold">{project.frequency}</span>
          </Badge>
          <Badge variant="info" className={project.isFrozen ? '' : 'hidden'}>
            Frozen
          </Badge>
          {organisationId != null && project.canEditProject && (
            <OrgProjectForm
              organisationId={organisationId}
              selectedOrganisationProject={organisationProject}
              sheetTitle={`${project.name} > Edit project`}
              sheetDescription="Edit the details of the project."
              sheetTriggerTooltip="Edit project"
              sheetTrigger={
                <Button variant="ghost" size="icon">
                  <IconV2EditOutline className="size-6" />
                  <span className="sr-only">Edit project</span>
                </Button>
              }
            />
          )}
        </div>
      </PageHeader>
      <ProjectDetailsLayout
        className="min-h-0 flex-1 bg-transparent"
        projectId={project.projectId}
        leaders={project.leaders}
        administrators={project.administrators}
        members={project.members}
        frequency={project.frequency}
        organisationId={organisationId}
        selectedProject={organisationProject}
        canEditProjectLeaders={orgPermissions?.canEditProjectLeaders ?? false}
        canEditProjectAdmins={project.canEditProjectAdmins}
        canEditMembers={project.canEditMembers}
        scrollDetails
      >
        <ProjectLinkedWorkspacesPanel scrollable>
          <ProjectWorkspacesSection
            className="rounded-lg bg-background pb-3 pl-4 pt-4"
            workspaces={project.workspaces}
            projectId={project.projectId}
            organisationId={organisationId ?? 0}
            projectName={organisationId != null ? project.name : undefined}
            canCreateWorkspace={project.canCreateWorkspace}
            canRequestWorkspace={organisationId != null}
            excludedAppIds={project.workspaces.map((w) => w.appId).filter((id): id is number => id != null && id > 0)}
          />
        </ProjectLinkedWorkspacesPanel>
      </ProjectDetailsLayout>
    </div>
  )
}
